import { Input } from "@/app/admin/_common/ui/input";
import { Textarea } from "@/app/admin/_common/ui/textarea";
import { Switch } from "@/app/admin/_common/ui/switch";
import { Label } from "@/app/admin/_common/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/admin/_common/ui/select";
import type { AutomationSchema, AutomationTemplate } from "@/app/_services/AutomationService";

type ConfigField = AutomationSchema["actions"][number]["config_fields"][number];

// Radix rejects an empty SelectItem value, so "use the first template" needs a stand-in.
const DEFAULT_TEMPLATE = "__default__";

/**
 * Renders an action's config form from its declared fields.
 *
 * This is the payoff of the schema endpoint: adding a ConfigField on the server grows a
 * working input here with its label, default and help text, and no change to this file.
 * The switch is on field.type, never on the action key — nothing here knows what
 * send_ticket_email is.
 */
export default function ActionConfigFields({
  fields,
  values,
  onChange,
  disabled,
  templates = [],
}: {
  fields: ConfigField[];
  values: Record<string, any>;
  onChange: (name: string, value: any) => void;
  disabled?: boolean;
  templates?: AutomationTemplate[];
}) {
  if (fields.length === 0) {
    return (
      <p className="text-[12px] text-muted-foreground">
        This action has nothing to configure.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {fields.map((field) => {
        const value = values?.[field.name] ?? field.default ?? "";
        const id = `action-config-${field.name}`;

        return (
          <div key={field.name} className="flex flex-col gap-1.5">
            <Label htmlFor={id} className="text-[12.5px]">
              {field.label}
              {field.required && <span className="ml-1 text-red-600">*</span>}
            </Label>

            {field.type === "boolean" ? (
              <div className="flex items-center gap-2">
                <Switch
                  id={id}
                  checked={Boolean(values?.[field.name] ?? field.default)}
                  disabled={disabled}
                  onCheckedChange={(checked) => onChange(field.name, checked)}
                />
              </div>
            ) : field.type === "select" ? (
              <Select
                value={String(value)}
                onValueChange={(next) => onChange(field.name, next)}
                disabled={disabled}
              >
                <SelectTrigger id={id} className="max-w-[320px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(field.options ?? []).map((option: any) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : field.type === "template_ref" && templates.length > 0 ? (
              <Select
                value={String(value) || DEFAULT_TEMPLATE}
                onValueChange={(next) =>
                  onChange(field.name, next === DEFAULT_TEMPLATE ? "" : next)
                }
                disabled={disabled}
              >
                <SelectTrigger id={id} className="max-w-[420px]">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {!field.required && (
                    <SelectItem value={DEFAULT_TEMPLATE}>
                      Default (first template)
                    </SelectItem>
                  )}
                  {templates.map((template) => (
                    <SelectItem key={template.key} value={template.key}>
                      {template.key} ({template.channel})
                    </SelectItem>
                  ))}
                  {/* A saved key with no matching template stays visible and labelled,
                      rather than the trigger silently going blank. */}
                  {String(value) &&
                    !templates.some((t) => t.key === String(value)) && (
                      <SelectItem value={String(value)}>
                        {String(value)} — missing
                      </SelectItem>
                    )}
                </SelectContent>
              </Select>
            ) : field.type === "text" || field.type === "html" ? (
              <Textarea
                id={id}
                value={String(value)}
                disabled={disabled}
                rows={4}
                onChange={(e) => onChange(field.name, e.target.value)}
              />
            ) : field.type === "number" ? (
              <Input
                id={id}
                type="number"
                value={String(value)}
                disabled={disabled}
                className="max-w-[200px]"
                onChange={(e) =>
                  onChange(field.name, e.target.value === "" ? "" : Number(e.target.value))
                }
              />
            ) : (
              // string, recipient_expression, a template_ref on an automation with no
              // templates yet, and anything the server adds later all fall through to a
              // text input rather than disappearing.
              <Input
                id={id}
                value={String(value)}
                disabled={disabled}
                className="max-w-[420px]"
                onChange={(e) => onChange(field.name, e.target.value)}
              />
            )}

            {field.help && (
              <p className="text-[11.5px] text-muted-foreground">{field.help}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
